import * as THREE from "three";
import { LIMITS } from "./pose";
import { FOREARM, SHIN, THIGH, UPPER_ARM, type Arm, type Leg } from "./skater-parts";

// Two-bone IK for the rider's legs and arms, used by `skater-model.ts` and
// `walk-pose.ts`.

/** The legs never straighten all the way, or the feet would leave the board. */
export const MAX_HIP = THIGH + SHIN - 0.01;
/** The hips never drop below this over the feet, so the knees stay in their limit and the legs can't flip. */
export const MIN_HIP = (THIGH + SHIN) * Math.cos(LIMITS.knee / 2) + 0.01;

/** Two-bone IK: plant the foot on the board at (0, 0, leg.z + shiftZ) given the hip height over it. */
export function solveLeg(leg: Leg, hipY: number, shiftZ = 0): void {
  const dz = leg.z + shiftZ - leg.hip.position.z;
  // A board pulled up past the hips (a deep grab) can't fold the legs past their limit or flip them over.
  const y = Math.max(MIN_HIP, hipY);
  const d = Math.min(THIGH + SHIN - 1e-3, Math.hypot(y, dz));
  // Knees bend backward only, and no further than their limit.
  const bend = Math.min(LIMITS.knee / 2, Math.acos(Math.min(1, d / (THIGH + SHIN))));
  const splay = -Math.atan2(dz, y);
  leg.hip.rotation.set(splay, 0, -bend, "XYZ");
  leg.knee.rotation.set(0, 0, 2 * bend);
  leg.foot.rotation.set(0, 0, -bend);
}

const DOWN = new THREE.Vector3(0, -1, 0);
/** Shoulder-to-hand distance with the elbow at its limit. */
const MIN_REACH = Math.sqrt(UPPER_ARM ** 2 + FOREARM ** 2 + 2 * UPPER_ARM * FOREARM * Math.cos(LIMITS.elbow)) + 1e-3;

/**
 * Two-bone IK: turns the shoulder and elbow so the hand lands on `targetW`
 * (world space), the elbow bending out toward the arm's pole. Past arm's
 * length the arm straightens and points at it. World matrices must be current.
 */
export function reachTo(arm: Arm, targetW: THREE.Vector3): void {
  const torso = arm.shoulder.parent!;
  const s = arm.shoulder.position;
  const v = torso.worldToLocal(targetW.clone()).sub(s);
  const len = v.length();
  const dir = len > 1e-6 ? v.clone().divideScalar(len) : DOWN.clone();
  // The elbow folds no further than its limit.
  const d = Math.min(UPPER_ARM + FOREARM - 1e-4, Math.max(MIN_REACH, len));
  // Law of cosines: the angle between the upper arm and the shoulder-to-hand line.
  const cosA = (UPPER_ARM ** 2 + d ** 2 - FOREARM ** 2) / (2 * UPPER_ARM * d);
  const a = Math.acos(Math.max(-1, Math.min(1, cosA)));
  const perp = arm.pole.clone().addScaledVector(dir, -arm.pole.dot(dir));
  if (perp.lengthSq() < 1e-8) perp.set(1, 0, 0).addScaledVector(dir, -dir.x);
  perp.normalize();
  const upper = dir.clone().multiplyScalar(Math.cos(a)).addScaledVector(perp, Math.sin(a));
  arm.shoulder.quaternion.setFromUnitVectors(DOWN, upper);
  // The forearm runs from the elbow to the hand; turn it in the upper arm's own frame.
  const fore = dir.multiplyScalar(d).addScaledVector(upper, -UPPER_ARM).normalize();
  fore.applyQuaternion(arm.shoulder.quaternion.clone().invert());
  arm.elbow.quaternion.setFromUnitVectors(DOWN, fore);
}
